import { store } from "../../store";
import { loginRdx } from "../../store/redux_auth/authSlice";
import { ApiClient } from "../../api/ApiClient";
import { accountApi } from "../../api/account";
import {
  deleteReferralCookie,
  deleteAccountCreationCookie,
  getAccountCreationCookie,
  getReferralCookie,
} from "../cookies/cookieHelpers";
import {
  trackSignIn,
  trackSignUp,
} from "../../components/tracking/utils/events";
import { postAppleLogin } from "../../api/auth/appleLogin";
import { postGoogleLogin } from "../../api/auth/googleLogin";
import { associateAuthenticatedInstallationIfNative } from "../native/installationAssociation";
import {
  getAppleAuthErrorMessage,
  isAppleAuthDisabledError,
} from "./appleAuthErrors";

export function buildUserDataFromAuthResponse(resData) {
  return {
    user_name: resData.user_name,
    email: resData.email,
    user_role: resData.user_role,
    has_usable_password: resData.has_usable_password,
    preferences: resData.preferences,
    account_status: resData.account_status,
    stripe_connected_account: resData.stripe_connected_account,
    affiliate_status: resData.affiliate_status || null,
    affiliates_enabled: resData.affiliates_enabled || false,
    financial_insights: resData.financial_insights,
    ista: resData.ista || false,
  };
}

export function getAuthErrorMessage(data, fallbackMessage) {
  if (!data) {
    return fallbackMessage;
  }

  return (
    data.message ||
    data.detail ||
    (data.non_field_errors && data.non_field_errors[0]) ||
    fallbackMessage
  );
}

/**
 * Finalize a successful social-auth API response into a Plaintr session.
 * Provider-specific exchange must already have succeeded.
 *
 * UNRESOLVED (Phase 4/5 / plaintr-api decisions, not Phase 3):
 * - This function is called from both the Trusted Browser and the
 *   WebView. Depending on the final Phase 4/5 bridge flow, it may run
 *   twice for one mobile login, double-firing trackSignIn/trackSignUp.
 *   Which context (if any) should own that tracking call is undecided.
 * - `MobileAuthHandoffRedeemView` (plaintr-api) always returns
 *   `created_user: false`, regardless of the original transaction type.
 *   Whether it should instead preserve the real value is an open
 *   plaintr-api contract question.
 */
export async function finalizeSocialLoginSession(data) {
  const userData = buildUserDataFromAuthResponse(data);

  ApiClient.initializeApiClient();

  // Authenticated session exists at this point.
  store.dispatch(loginRdx(userData));

  // Fire-and-forget; must never delay or block login.
  void associateAuthenticatedInstallationIfNative();

  if (data.created_user) {
    trackSignUp();
  } else {
    trackSignIn();
  }

  store.dispatch(accountApi.util.invalidateTags(["UNAUTHORIZED"]));
  window.dispatchEvent(new Event("session-restored"));

  // Refresh CSRF without blocking navigation to the feed.
  void ApiClient.getCsrfToken(true).catch((error) => {
    console.error("Unable to refresh CSRF token after social login:", error);
  });

  const referralCode = getReferralCookie();
  if (referralCode) {
    deleteReferralCookie();
  }

  const accountCreationRef = getAccountCreationCookie();
  if (accountCreationRef) {
    deleteAccountCreationCookie();
  }

  return { ok: true, data };
}

/**
 * Run a provider exchange, then complete the shared Plaintr login session.
 *
 * @param {object} options
 * @param {(args: { referralCode: string|null }) => Promise<{response: Response, data: object|null}>} options.exchange
 * @param {string} options.fallbackErrorMessage
 * @param {(data: object|null, fallbackMessage: string) => string} [options.mapErrorMessage]
 * @param {(failure: { status: number, data: object|null }) => object} [options.enrichFailure]
 * @param {(args: { status: number, data: object|null, referralCode: string|null }) => void} [options.onExchangeFailure]
 */
export async function completeSocialLogin({
  exchange,
  fallbackErrorMessage,
  mapErrorMessage = getAuthErrorMessage,
  enrichFailure,
  onExchangeFailure,
}) {
  const referralCode = getReferralCookie();
  const { response, data } = await exchange({ referralCode });

  if (!response.ok) {
    onExchangeFailure?.({
      status: response.status,
      data,
      referralCode,
    });

    return {
      ok: false,
      status: response.status,
      data,
      message: mapErrorMessage(data, fallbackErrorMessage),
      ...(enrichFailure?.({ status: response.status, data }) || {}),
    };
  }

  return finalizeSocialLoginSession(data);
}

export async function completeGoogleLogin({
  code,
  termsAccepted = false,
  mobileTransaction = false,
}) {
  return completeSocialLogin({
    fallbackErrorMessage: "Unable to sign in with Google. Please try again.",
    exchange: ({ referralCode }) =>
      postGoogleLogin({
        code,
        termsAccepted,
        referralCode,
        mobileTransaction,
      }),
    onExchangeFailure: ({ status, data }) => {
      console.error("Google login API failed", {
        status,
        data,
        origin: window.location.origin,
        codeFingerprint: code
          ? `${code.slice(0, 8)}...${code.slice(-6)}`
          : null,
      });
    },
  });
}

export async function completeAppleLogin({
  code,
  state,
  redirectUri,
  termsAccepted = false,
  firstName,
  lastName,
  mobileTransaction = false,
}) {
  return completeSocialLogin({
    fallbackErrorMessage: "Unable to sign in with Apple. Please try again.",
    mapErrorMessage: getAppleAuthErrorMessage,
    enrichFailure: ({ status, data }) => ({
      disabled: isAppleAuthDisabledError(status, data),
    }),
    exchange: ({ referralCode }) =>
      postAppleLogin({
        code,
        state,
        redirectUri,
        termsAccepted,
        referralCode,
        firstName,
        lastName,
        mobileTransaction,
      }),
    onExchangeFailure: ({ status, data }) => {
      console.error("Apple login API failed", {
        status,
        code: data?.code || null,
        origin: window.location.origin,
      });
    },
  });
}
